import { Message, message } from 'antd';
import { useState } from 'react';
import {
	CREATE_BADGE_ASSIGNMENT,
	UPDATE_BADGE_ASSIGNMENT,
	ADD_REMOVE_CLAP,
	UPDATE_USER_RECOGNITION_EVENTS,
} from 'src/_shared/api/graphql/custom/recognition/recognitionMutations';
import DashboardPresentation from '../../presentation/dashboardPresentation/index.js';
import { useUserStatsAndRankings } from 'src/_shared/hooks/useUserStatsAndRankings.js';
import { useMutation } from '@apollo/client';
import uuid from 'uuid/v4';

import useInfiniteBadgeAssignments from './hooks/useInfiniteBadgeAssignments.js';

const DashboardContainer = (props) => {
	const currentUser = props?.currentUser || {};
	const { userStats, leaderboards, userStatsLoading } = useUserStatsAndRankings(
		currentUser?.id
	);
	const [isOpenRewardModel, setIsOpenRewardModel] = useState(false);
	const [sendRewardObject, setSendRewardObject] = useState({});
	const [fullAccountClaim, setFullAccountClaim] = useState([]);
	const [isShoutOutSend, setIsShoutOutSend] = useState(false);
	const [isEmployeeSelected, setIsEmployeeSelected] = useState(false);
	const [recipients, setRecipients] = useState([]);
	const [selectedTags, setSelectedTags] = useState([]);
	const [updateUserRecognitionEvents] = useMutation(
		UPDATE_USER_RECOGNITION_EVENTS,
		{
			variables: {
				input: {
					id: currentUser.id,
					recognitionEvents: currentUser?.recognitionEvents,
				},
			},
		}
	);
	const handleHasSeenPopup = async () => {
		await updateUserRecognitionEvents({
			variables: {
				input: {
					id: currentUser.id,
					recognitionEvents: {
						...currentUser.recognitionEvents,
						hasSeenPopup: true,
					},
				},
			},
		});
	};
	const [createBadgeAssignment] = useMutation(CREATE_BADGE_ASSIGNMENT);
	const [updateBadgeAssignmentMutation] = useMutation(UPDATE_BADGE_ASSIGNMENT);
	const [addRemoveClap] = useMutation(ADD_REMOVE_CLAP);

	// With the Milestones tab on, automated milestone awards only show there.
	const excludeAutomated = Boolean(
		currentUser?.featureFlags?.includes('enableMilestonesTab')
	);

	const {
		allBadgeAssignments: newestFeedData,
		badgeAssignmentsLoading: newestFeedLoading,
		hasMoreData: hasMoreNewestFeedData,
		isLoadingMore: isLoadingMoreNewestFeed,
		loadMoreBadgeAssignments: loadMoreNewestFeed,
		resetBadgeAssignments: resetNewestFeed,
		refetchBadgeAssignments: refetchNewestFeed,
		updateBadgeAssignment: updateNewestFeedAssignment,
	} = useInfiniteBadgeAssignments({
		currentUser,
		feedType: 'newest',
		pageSize: 25,
		excludeAutomated,
	});

	const {
		allBadgeAssignments: topFeedData,
		badgeAssignmentsLoading: topFeedLoading,
		hasMoreData: hasMoreTopFeedData,
		isLoadingMore: isLoadingMoreTopFeed,
		loadMoreBadgeAssignments: loadMoreTopFeed,
		resetBadgeAssignments: resetTopFeed,
		refetchBadgeAssignments: refetchTopFeed,
		updateBadgeAssignment: updateTopFeedAssignment,
	} = useInfiniteBadgeAssignments({
		currentUser,
		feedType: 'top',
		pageSize: 25,
		excludeAutomated,
	});

	const {
		allBadgeAssignments: teamFeedData,
		badgeAssignmentsLoading: teamFeedLoading,
		hasMoreData: hasMoreTeamFeedData,
		isLoadingMore: isLoadingMoreTeamFeed,
		loadMoreBadgeAssignments: loadMoreTeamFeed,
		resetBadgeAssignments: resetTeamFeed,
		refetchBadgeAssignments: refetchTeamFeed,
		updateBadgeAssignment: updateTeamFeedAssignment,
	} = useInfiniteBadgeAssignments({
		currentUser,
		feedType: 'subCompany',
		pageSize: 25,
		excludeAutomated,
	});

	const {
		allBadgeAssignments: milestonesFeedData,
		badgeAssignmentsLoading: milestonesFeedLoading,
		hasMoreData: hasMoreMilestonesFeedData,
		isLoadingMore: isLoadingMoreMilestonesFeed,
		loadMoreBadgeAssignments: loadMoreMilestonesFeed,
		resetBadgeAssignments: resetMilestonesFeed,
		refetchBadgeAssignments: refetchMilestonesFeed,
		updateBadgeAssignment: updateMilestonesFeedAssignment,
	} = useInfiniteBadgeAssignments({
		currentUser,
		feedType: 'milestones',
		pageSize: 25,
	});

	const {
		allBadgeAssignments: tagsFeedData,
		badgeAssignmentsLoading: tagsFeedLoading,
		hasMoreData: hasMoreTagsFeedData,
		isLoadingMore: isLoadingMoreTagsFeed,
		loadMoreBadgeAssignments: loadMoreTagsFeed,
		resetBadgeAssignments: resetTagsFeed,
		refetchBadgeAssignments: refetchTagsFeed,
		updateBadgeAssignment: updateTagsFeedAssignment,
	} = useInfiniteBadgeAssignments({
		currentUser,
		feedType: 'tags',
		tags: selectedTags,
		pageSize: 25,
		excludeAutomated,
	});

	const tagsFeed = {
		tagsFeedData,
		tagsFeedLoading,
		hasMoreTagsFeedData,
		isLoadingMoreTagsFeed,
		loadMoreTagsFeed,
		resetTagsFeed,
		refetchTagsFeed,
		updateTagsFeedAssignment,
	};

	const handleReplySubmit = async (badgeAssignmentId, replyText) => {
		const currentAssignment =
			newestFeedData.find((item) => item.id === badgeAssignmentId) ||
			topFeedData.find((item) => item.id === badgeAssignmentId) ||
			teamFeedData.find((item) => item.id === badgeAssignmentId) ||
			milestonesFeedData.find((item) => item.id === badgeAssignmentId) ||
			tagsFeed.tagsFeedData.find((item) => item.id === badgeAssignmentId);

		if (!currentAssignment) {
			message.error('Failed to add reply. Please try again.');
			return false;
		}

		const previousComments = currentAssignment.comments || '[]';
		const currentComments = JSON.parse(previousComments);

		const newComment = {
			userId: currentUser?.id,
			firstName: currentUser?.firstName,
			lastName: currentUser?.lastName,
			avatarKey: currentUser?.avatar?.key || null,
			message: replyText,
			createdAt: new Date().toISOString(),
		};

		const updatedComments = [...currentComments, newComment];
		const commentsJson = JSON.stringify(updatedComments);

		// Optimistic update
		updateNewestFeedAssignment(badgeAssignmentId, {
			comments: commentsJson,
		});
		updateTopFeedAssignment(badgeAssignmentId, {
			comments: commentsJson,
		});
		updateTeamFeedAssignment(badgeAssignmentId, {
			comments: commentsJson,
		});
		updateMilestonesFeedAssignment(badgeAssignmentId, {
			comments: commentsJson,
		});
		tagsFeed.updateTagsFeedAssignment(badgeAssignmentId, {
			comments: commentsJson,
		});

		try {
			const { data } = await updateBadgeAssignmentMutation({
				variables: {
					input: {
						id: badgeAssignmentId,
						companyId: currentUser?.company?.id,
						comments: commentsJson,
					},
				},
			});

			if (data?.updateBadgeAssignment) {
				message.success('Reply added successfully');
				return true;
			}
		} catch (error) {
			console.error('Error submitting reply:', error);
		}

		// Rollback optimistic update on failure
		message.error('Failed to add reply. Please try again.');
		const rollback = { comments: previousComments };
		updateNewestFeedAssignment(badgeAssignmentId, rollback);
		updateTopFeedAssignment(badgeAssignmentId, rollback);
		updateTeamFeedAssignment(badgeAssignmentId, rollback);
		updateMilestonesFeedAssignment(badgeAssignmentId, rollback);
		tagsFeed.updateTagsFeedAssignment(badgeAssignmentId, rollback);
		return false;
	};

	const handleSetToPrivate = async (badgeAssignmentId) => {
		try {
			const currentAssignment =
				newestFeedData.find((item) => item.id === badgeAssignmentId) ||
				topFeedData.find((item) => item.id === badgeAssignmentId) ||
				teamFeedData.find((item) => item.id === badgeAssignmentId) ||
				milestonesFeedData.find((item) => item.id === badgeAssignmentId) ||
				tagsFeed.tagsFeedData.find((item) => item.id === badgeAssignmentId);

			if (!currentAssignment) {
				throw new Error('Badge assignment not found');
			}

			const { data } = await updateBadgeAssignmentMutation({
				variables: {
					input: {
						id: badgeAssignmentId,
						companyId: currentUser?.company?.id,
						isPrivate: true,
					},
				},
			});

			if (data?.updateBadgeAssignment) {
				await resetNewestFeed();
				await resetTopFeed();
				await resetTeamFeed();
				await resetMilestonesFeed();
				await tagsFeed.resetTagsFeed();

				message.success('Recognition was successfully updated to private.');
				fetchInitialData(true);
			}
		} catch (error) {
			console.error('Error submitting request:', error);
			message.error('Failed to update Recognition. Please try again.');
		}
	};

	const handleClapClick = async (badgeAssignmentId) => {
		try {
			const currentAssignment =
				newestFeedData.find((item) => item.id === badgeAssignmentId) ||
				topFeedData.find((item) => item.id === badgeAssignmentId) ||
				teamFeedData.find((item) => item.id === badgeAssignmentId) ||
				milestonesFeedData.find((item) => item.id === badgeAssignmentId) ||
				tagsFeed.tagsFeedData.find((item) => item.id === badgeAssignmentId);

			if (currentAssignment) {
				const hasClapped =
					currentAssignment.hasClappedUsers?.some(
						(user) => user.id === currentUser?.id
					) || false;

				const updatedHasClappedUsers = hasClapped
					? currentAssignment.hasClappedUsers.filter(
							(user) => user.id !== currentUser?.id
						)
					: [
							...(currentAssignment.hasClappedUsers || []),
							{
								id: currentUser?.id,
								firstName: currentUser?.firstName,
								lastName: currentUser?.lastName,
							},
						];

				const updatedTotalClaps = hasClapped
					? (currentAssignment.totalClaps || 1) - 1
					: (currentAssignment.totalClaps || 0) + 1;

				updateNewestFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: updatedHasClappedUsers,
					totalClaps: updatedTotalClaps,
				});
				updateTopFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: updatedHasClappedUsers,
					totalClaps: updatedTotalClaps,
				});
				updateTeamFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: updatedHasClappedUsers,
					totalClaps: updatedTotalClaps,
				});
				updateMilestonesFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: updatedHasClappedUsers,
					totalClaps: updatedTotalClaps,
				});
				tagsFeed.updateTagsFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: updatedHasClappedUsers,
					totalClaps: updatedTotalClaps,
				});
			}

			const { data } = await addRemoveClap({
				variables: {
					badgeAssignmentId: badgeAssignmentId,
					userId: currentUser?.id,
				},
			});

			if (data?.addRemoveClap) {
				updateNewestFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: data.addRemoveClap.hasClappedUsers,
					totalClaps: data.addRemoveClap.totalClaps,
				});
				updateTopFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: data.addRemoveClap.hasClappedUsers,
					totalClaps: data.addRemoveClap.totalClaps,
				});
				updateTeamFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: data.addRemoveClap.hasClappedUsers,
					totalClaps: data.addRemoveClap.totalClaps,
				});
				updateMilestonesFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: data.addRemoveClap.hasClappedUsers,
					totalClaps: data.addRemoveClap.totalClaps,
				});
				tagsFeed.updateTagsFeedAssignment(badgeAssignmentId, {
					hasClappedUsers: data.addRemoveClap.hasClappedUsers,
					totalClaps: data.addRemoveClap.totalClaps,
				});

				return true;
			}
		} catch (error) {
			console.error('Error toggling clap:', error);
			message.error('Failed to update clap. Please try again.');

			if (refetchNewestFeed) {
				refetchNewestFeed();
				refetchTopFeed();
				refetchTeamFeed();
				refetchMilestonesFeed();
				tagsFeed.refetchTagsFeed();
			}
			return false;
		}
	};

	const fetchInitialData = (isNewPost = false) => {
		if (isNewPost) {
			if (refetchNewestFeed) refetchNewestFeed();
			if (refetchTopFeed) refetchTopFeed();
			if (refetchTeamFeed) refetchTeamFeed();
			if (refetchMilestonesFeed) refetchMilestonesFeed();
			if (tagsFeed?.refetchTagsFeed) tagsFeed.refetchTagsFeed();
		}
	};

	const handleSendShoutout = async () => {
		if (
			!sendRewardObject?.employeeDetail &&
			!sendRewardObject?.accountClaimId
		) {
			setIsEmployeeSelected(true);
			return;
		}
		if (
			(!sendRewardObject?.content ||
				sendRewardObject?.content?.message?.trim().length === 0) &&
			!sendRewardObject?.rewardtype
		) {
			Message.error('Please add recognition or message to assign.');
			return;
		}

		const isNotPreApproved =
			sendRewardObject?.selectedBadge?.id &&
			!sendRewardObject?.selectedBadge?.isPreApproved;
		const requireAdminApproval =
			currentUser?.company?.recognitionSettings?.requireAdminApproval;

		try {
			setIsShoutOutSend(true);

			const newBadgeAssignmentId = uuid();

			const payload = {
				variables: {
					input: {
						itemType: 'BadgeAssignment',
						id: newBadgeAssignmentId,
						assignmentId: newBadgeAssignmentId,
						accountClaimId:
							recipients?.length > 0
								? recipients[0]?.accountClaimId || null
								: null,
						badgeId: sendRewardObject?.selectedBadge?.id,
						companyId: currentUser?.company?.id,
						userId:
							recipients?.length > 0
								? recipients[0]?.userId || 'account_claim'
								: sendRewardObject?.employeeDetail !== 'account_claim_selected'
									? sendRewardObject?.employeeDetail
									: 'account_claim',
						awardedBy: currentUser?.id,
						managerAssigned: false,
						points: sendRewardObject?.selectedBadge?.points,
						rewardBonus: sendRewardObject?.selectedBadge?.rewardBonus,
						recipients: recipients,
						approvalStatus:
							isNotPreApproved || requireAdminApproval ? 'Pending' : 'Approved',
						content: {
							message: sendRewardObject?.content?.message?.trim(),
							gifURL: sendRewardObject?.content?.gifURL,
							tags: sendRewardObject?.content?.tags || [],
						},
					},
				},
			};

			await createBadgeAssignment(payload);

			if (resetNewestFeed) {
				await resetNewestFeed();
				await resetTopFeed();
				await resetTeamFeed();
				await resetMilestonesFeed();
				await tagsFeed.resetTagsFeed();
			}

			setIsShoutOutSend(false);
			Message.success(
				isNotPreApproved || requireAdminApproval
					? 'Recognition Successfully Sent for Approval'
					: 'Recognition Successfully Sent'
			);
			setSendRewardObject(null);
			fetchInitialData(true);
		} catch (error) {
			console.error('Error in handleSendShoutout:', error);
			setIsShoutOutSend(false);
			message.error('Failed to create recognition! Try again later.');
		}
	};

	return (
		<div>
			<DashboardPresentation
				sendRewardObject={sendRewardObject}
				setSendRewardObject={setSendRewardObject}
				fullAccountClaim={fullAccountClaim}
				setFullAccountClaim={setFullAccountClaim}
				handleHasSeenPopup={handleHasSeenPopup}
				isOpenRewardModel={isOpenRewardModel}
				setIsOpenRewardModel={setIsOpenRewardModel}
				isShoutOutSend={isShoutOutSend}
				handleSendShoutout={handleSendShoutout}
				isEmployeeSelected={isEmployeeSelected}
				setIsEmployeeSelected={setIsEmployeeSelected}
				recipients={recipients}
				setRecipients={setRecipients}
				handleReplySubmit={handleReplySubmit}
				handleSetToPrivate={handleSetToPrivate}
				handleClapClick={handleClapClick}
				newestFeedData={newestFeedData}
				topFeedData={topFeedData}
				teamFeedData={teamFeedData}
				milestonesFeedData={milestonesFeedData}
				newestFeedLoading={newestFeedLoading}
				topFeedLoading={topFeedLoading}
				teamFeedLoading={teamFeedLoading}
				milestonesFeedLoading={milestonesFeedLoading}
				hasMoreNewestFeedData={hasMoreNewestFeedData}
				hasMoreTopFeedData={hasMoreTopFeedData}
				hasMoreTeamFeedData={hasMoreTeamFeedData}
				hasMoreMilestonesFeedData={hasMoreMilestonesFeedData}
				isLoadingMoreNewestFeed={isLoadingMoreNewestFeed}
				isLoadingMoreTopFeed={isLoadingMoreTopFeed}
				isLoadingMoreTeamFeed={isLoadingMoreTeamFeed}
				isLoadingMoreMilestonesFeed={isLoadingMoreMilestonesFeed}
				loadMoreNewestFeed={loadMoreNewestFeed}
				loadMoreTopFeed={loadMoreTopFeed}
				loadMoreTeamFeed={loadMoreTeamFeed}
				loadMoreMilestonesFeed={loadMoreMilestonesFeed}
				tagsFeed={tagsFeed}
				selectedTags={selectedTags}
				setSelectedTags={setSelectedTags}
				recognitionPointsTotal={userStats.recognitionPointsTotal}
				recognitionPointsLeaderboard={leaderboards.recognitionPoints}
				recognitionPointsBySubCompanyLeaderboard={
					leaderboards.recognitionPointsBySubCompany
				}
				userStatsLoading={userStatsLoading}
			/>
		</div>
	);
};
export default DashboardContainer;
